import { z } from 'zod';
import { InsightSourceSchema } from './insights-schema.js';
import { RequirementGapSchema, RequesterTypeSchema, RfqRequirementsDraftSchema } from './rfq-schema.js';
/**
 * KI-Bedarfsaufnahme. Siehe `docs/03_AI_CHAT.md`.
 *
 * Der Chat ist die Konversionsmaschine, nicht der Produktkern — aber er ist der einzige
 * öffentliche, unauthentifizierte LLM-Endpunkt im System und damit die größte Angriffsfläche.
 * Die Grenzen in {@link CHAT_LIMITS} sind serverseitig zu prüfen, nicht im Frontend (§A.6).
 *
 * **Personenbezug:** Kontaktdaten werden erst im `submit`-Schritt erhoben und gehen **nie** in
 * den Modellkontext. Was nie im Prompt stand, kann auch in keiner Antwort auftauchen.
 */
export const ChatRoleSchema = z.enum(['user', 'assistant']);
/**
 * Der erste Satz der Unterhaltung.
 *
 * Bewusst **fest verdrahtet und nicht generiert**: Er trägt den Transparenzhinweis nach
 * EU-KI-VO und kostet so weder einen Bedrock-Aufruf noch eine Sekunde Wartezeit.
 *
 * **Warum das hier steht und nicht in `prompts.ts`.** Die Marketing-Seite zeigt die Begrüßung,
 * **bevor** eine Session existiert — offener Chat beim Seitenaufruf, aber ohne reCAPTCHA-Prüfung
 * und ohne Datenbankschreibvorgang für jeden Crawler. Der Server schreibt anschließend
 * denselben Text als `seq: 1`. Zwei Kopien dieses Satzes wären zwei Texte, die auseinanderlaufen
 * — und der Besucher sähe nach dem ersten Absenden einen anderen als vorher.
 *
 * **Er sagt, wohin man schreibt.** „Unten ins Feld" ist für Erfahrene überflüssig und für
 * Besucher, die selten im Netz etwas eintippen, die eigentliche Auskunft. Die Fassung bis
 * Oktober 2026 („nehme Ihren Catering-Bedarf auf") klang nach Behörde und ließ genau das offen.
 *
 * **Er sagt, dass es nichts kostet und nicht bindet.** Die Auswertung der ersten echten Chats
 * (D38) zeigte: „kostenlos" fiel in keinem einzigen Gespräch, und wer nach dem Preis fragte,
 * schrieb „keine anfrage, ich möchte nur wissen …" — die Sorge, sich festzulegen, ist die
 * eigentliche Hürde. Deshalb steht die Antwort darauf im ersten Absatz und nicht erst im
 * Kleingedruckten der Nutzungsbedingungen.
 */
export const CHAT_GREETING = [
    'Guten Tag! Ich bin der KI-Assistent von caterinx und helfe Ihnen bei Ihrer Catering-Anfrage.',
    'Schreiben Sie mir einfach unten ins Feld, was Sie planen, zum Beispiel Anlass, Datum, Ort',
    'und wie viele Personen kommen. Den Rest frage ich nach.',
    'Für Sie ist das kostenlos und unverbindlich: Wir holen Angebote von Caterern aus Ihrer Region',
    'ein, und Sie entscheiden erst, wenn Sie verglichen haben.',
].join(' ');
/** Harte Grenzen je Session. Nicht verhandelbar (§A.6, Schicht 3). */
export const CHAT_LIMITS = {
    /** Maximale Anzahl Nutzer-Turns je Session. */
    maxTurns: 40,
    /** Maximale Zeichen je Nutzernachricht. */
    maxMessageChars: 2000,
    /** Kumulierte Input-Tokens je Session, über alle Turns. */
    maxCumulativeInputTokens: 60_000,
    /** Sessions je IP-Hash und Stunde (§A.6, Schicht 4). */
    maxSessionsPerIpPerHour: 3,
    /** Fenster des an das Modell übergebenen Verlaufs — der wichtigste Kostenhebel. */
    historyWindowTurns: 12,
};
/** TTL der Chat-Daten in Tagen. Chat-Sessions sind Wegwerf-Daten — RFQs sind es nicht (§4.4). */
export const CHAT_TTL_DAYS = 30;
export const ChatMessageSchema = z.object({
    sessionId: z.string().min(1),
    /** Fortlaufend ab 1. Trägt zugleich das optimistische Sperren gegen Doppelklicks (§6). */
    seq: z.int().min(1),
    role: ChatRoleSchema,
    content: z.string().max(CHAT_LIMITS.maxMessageChars),
    createdAt: z.iso.datetime(),
});
export const ChatSessionStatusSchema = z.enum([
    'active',
    /** Grenze aus {@link CHAT_LIMITS} erreicht — höfliche Abschlussnachricht, keine Turns mehr. */
    'limit_reached',
    /** In eine RFQ überführt. Die Session bleibt zur Nachvollziehbarkeit bestehen. */
    'submitted',
    'abandoned',
]);
export const ChatSessionSchema = z.object({
    sessionId: z.string().min(1),
    status: ChatSessionStatusSchema,
    /** SHA-256 des `publicToken`. Das Token selbst wird nie gespeichert (Muster: `shareToken.ts`). */
    publicTokenHash: z.string().length(64),
    /** Der schrittweise befüllte Anforderungsentwurf — jedes Feld optional, bis es gesetzt ist. */
    requirementsDraft: RfqRequirementsDraftSchema.default({}),
    /**
     * Wann der Pflichtkern erstmals vollständig war — der Moment, in dem die Island das
     * Kontaktformular einblendet.
     *
     * Bewusst ein Zeitstempel und **kein Status**: Nach dem „fertig" darf weitergeredet werden
     * („doch 60 Personen"), die Session bleibt also `active`. Ein eigener Status hätte entweder
     * weitere Turns gesperrt oder müsste bei jeder Korrektur zurückgesetzt werden.
     */
    readyAt: z.iso.datetime().nullable().default(null),
    turnCount: z.int().min(0).default(0),
    cumulativeInputTokens: z.int().min(0).default(0),
    cumulativeOutputTokens: z.int().min(0).default(0),
    /** Letzte vergebene `seq` — Grundlage des optimistischen Sperrens. */
    lastSeq: z.int().min(0).default(0),
    /** Ab ~100 KB wandert der Rohverlauf nach S3 (§4.3). */
    transcriptS3Key: z.string().nullable().default(null),
    rfqId: z.string().nullable().default(null),
    /** Normalisierter Seitenpfad, auf dem das Gespräch begann. */
    entryPath: z.string().nullable().default(null),
    /** Herkunftskategorie des Besuchs. */
    source: InsightSourceSchema.nullable().default(null),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    /** Unix-Sekunden. Läuft 30 Tage nach der **letzten Aktivität** ab, nicht nach dem Anlegen. **Nur** hier und auf den Nachrichten erlaubt (§4.4). */
    expiresAt: z.int().min(0),
});
/* --------------------------------------------------------------------------------------------
 * Tool Use (D5) — statt Freitext-Extraktion
 * ----------------------------------------------------------------------------------------- */
/**
 * `save_requirements(partial)` — das Modell ruft es nach jedem Erkenntnisgewinn auf, auch mit
 * Teilangaben. Der Handler merged, validiert und gibt den Stand plus die fehlenden Pflichtfelder
 * zurück; dieser Rückgabewert steuert das weitere Gespräch.
 */
export const SaveRequirementsInputSchema = RfqRequirementsDraftSchema;
export const SaveRequirementsResultSchema = z.object({
    saved: RfqRequirementsDraftSchema,
    /** Die noch fehlenden Pflichtfelder. Leer = `submit_request` ist zulässig. */
    missingFields: z.array(RequirementGapSchema),
    ready: z.boolean(),
});
/**
 * `submit_request({ summary })` — der Übergabepunkt an das Kontaktformular.
 *
 * **Abweichung von `03_AI_CHAT.md` §A.2, und zwar eine bewusste.** Die Fassung dort skizziert
 * `submit_request(contact)`, also das Modell reicht Name, E-Mail und Firma durch. Das steht im
 * direkten Widerspruch zu §A.6 („Kontaktdaten … gehen **nie** in den Modellkontext"): Was das
 * Modell als Werkzeugargument schreibt, hat es vorher als Text erzeugt, und was es einmal
 * erzeugt hat, kann in jeder späteren Antwort wieder auftauchen.
 *
 * Deshalb hier ohne Personenbezug: Das Werkzeug meldet nur „der Bedarf steht" und liefert eine
 * kurze Zusammenfassung für die Überschrift des Formulars. Die Kontaktdaten gehen aus dem
 * Browser **direkt** an `POST /chat/sessions/{id}/submit` — an der Lambda vorbei, die mit
 * Bedrock spricht.
 *
 * Aufrufbar ist es nur mit vollständigem Pflichtkern; **der Handler prüft das serverseitig
 * noch einmal**, das Modell ist hier nicht die letzte Instanz.
 */
export const SubmitRequestToolInputSchema = z.object({
    /** Ein bis zwei Sätze, die der Kunde über dem Kontaktformular wiedererkennt. */
    summary: z.string().min(1).max(400),
});
export const SubmitRequestToolResultSchema = z.object({
    ready: z.boolean(),
    missingFields: z.array(RequirementGapSchema),
});
/**
 * Die Kontaktdaten aus dem Formular — **nicht** aus dem Modell.
 *
 * Es gibt kein Kundenkonto im MVP (D6); die E-Mail-Adresse ist die Identität, und der
 * Magic-Link aus der Bestätigungsmail ist die Anmeldung.
 */
export const SubmitRequestInputSchema = z
    .object({
    email: z.email(),
    contactName: z.string().min(1).max(200),
    /**
     * Firma oder Organisation (D38). Default `business`, damit ein Formular aus der Zeit vor
     * D38, das noch im Browser-Cache liegt, weiterhin durchgeht.
     */
    requesterType: RequesterTypeSchema.default('business'),
    /** Pflicht für `business`, für `private` bedeutungslos und wird verworfen. */
    company: z.string().trim().max(200).nullable().default(null),
    phone: z.string().max(40).nullable().default(null),
    /** Zustimmung zu Nutzungsbedingungen und Verarbeitung — ohne sie keine Anfrage. */
    acceptedTerms: z.literal(true),
})
    .refine((input) => input.requesterType === 'private' || (input.company ?? '').length > 0, {
    path: ['company'],
    message: 'Bitte die Firma oder Organisation angeben.',
});
export const SubmitRequestResultSchema = z.object({
    rfqId: z.string().min(1),
    statusUrl: z.url(),
});
/** Die beiden Werkzeugnamen als Konstante — kein Stringliteral im Handler. */
export const CHAT_TOOL_NAMES = {
    saveRequirements: 'save_requirements',
    submitRequest: 'submit_request',
};
/* --------------------------------------------------------------------------------------------
 * REST-Verträge
 * ----------------------------------------------------------------------------------------- */
/** `POST /chat/sessions` — der reCAPTCHA-gesicherte Einstieg (§A.6, Schicht 1). */
export const CreateChatSessionInputSchema = z.object({
    recaptchaToken: z.string().min(1).nullable().default(null),
    /** Seite, auf der das Gespräch begann (roh `location.pathname`) — für die Auswertung. */
    // `.catch(null)`: Ein unbrauchbarer Messwert darf das Anlegen des Gesprächs nie verhindern.
    entryPath: z.string().max(200).nullable().default(null).catch(null),
    /** Herkunftskategorie des Besuchs, nie die `gclid` selbst. */
    source: InsightSourceSchema.nullable().default(null).catch(null),
});
export const CreateChatSessionResultSchema = z.object({
    sessionId: z.string().min(1),
    /** Ersetzt eine Anmeldung und bindet die Session an diesen Browser. Nur hier im Klartext. */
    publicToken: z.string().min(1),
    greeting: z.string().min(1),
});
/**
 * Was `GET /chat/sessions/{id}` zurückgibt — die Sicht für den Neuladen-Fall.
 *
 * Eine eigene Einschlussliste, kein `.omit()` auf {@link ChatSessionSchema}: Der
 * `publicTokenHash` und der S3-Zeiger haben im Browser nichts zu suchen, und ein später
 * ergänztes Feld wäre per Default unsichtbar statt per Default öffentlich.
 */
export const ChatSessionViewSchema = z.object({
    sessionId: z.string().min(1),
    status: ChatSessionStatusSchema,
    messages: z.array(z.object({ seq: z.int(), role: ChatRoleSchema, content: z.string() })),
    requirementsDraft: RfqRequirementsDraftSchema,
    missingFields: z.array(RequirementGapSchema),
    ready: z.boolean(),
    turnCount: z.int().min(0),
    rfqId: z.string().nullable(),
});
/** `POST chat.caterinx.com/turn` — der Streaming-Endpunkt (D4). */
export const ChatTurnInputSchema = z.object({
    sessionId: z.string().min(1),
    publicToken: z.string().min(1),
    message: z.string().min(1).max(CHAT_LIMITS.maxMessageChars),
});
/* --------------------------------------------------------------------------------------------
 * Transport (SSE)
 * ----------------------------------------------------------------------------------------- */
/** Ereignisse, die der Streaming-Endpunkt `POST chat.caterinx.com/turn` sendet. */
export const ChatStreamEventSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('delta'), text: z.string() }),
    z.object({ type: z.literal('tool_use'), name: z.string() }),
    /** Der Stand nach einem `save_requirements` — treibt die Fortschrittsanzeige. */
    z.object({
        type: z.literal('requirements'),
        draft: RfqRequirementsDraftSchema,
        missingFields: z.array(RequirementGapSchema),
    }),
    /** Der Pflichtkern steht: Die Island blendet das Kontaktformular ein. */
    z.object({ type: z.literal('ready'), summary: z.string() }),
    z.object({ type: z.literal('done'), seq: z.int().min(1) }),
    z.object({ type: z.literal('limit'), reason: z.enum(['turns', 'tokens', 'message_length']) }),
    z.object({ type: z.literal('error'), code: z.string(), message: z.string() }),
]);
